<script setup>
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import DOMPurify from 'dompurify';
import { debounce, isNil, keyBy } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useModal } from 'vue-final-modal';
import HawkHandsOnTable from '~/common/components/organisms/hawk-handsontable/hawk-handsontable.vue';
import { useMembers } from '~/common/composables/members.js';
import { stringToNumber } from '~/common/utils/common.utils.js';
import PmLeaveModal from '~/project-management/components/pm-leave-modal.vue';
import { useProjectManagementStore } from '~/project-management/store/pm.store';

const props = defineProps({
  activity: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const $t = inject('$t');
const $toast = inject('$toast');

const { getUserDetails } = useMembers();

const project_management_store = useProjectManagementStore();
const { $g, active_schedule, is_fullscreen } = storeToRefs(project_management_store);
const { update_trackings } = project_management_store;

const hot$ = ref(null);

const state = reactive({
  current_month: null,
  hot_data: [],
  hot_instance: null,
  activities: [],
  is_save_disabled: false,
  is_saving: false,
  force_update: 0,
  has_unsaved_changes: false,
  original_hot_data: null,
  is_maximized: false,
});

const unsaved_changes_modal = useModal({
  component: PmLeaveModal,
});

const schedule_resources_map = computed(() => {
  return keyBy(active_schedule.value.resources, 'uid');
});

const schedule_date_bounds = computed(() => {
  return {
    min_date: dayjs(active_schedule.value.planned_start),
    max_date: dayjs(),
  };
});

const min_month = computed(() => {
  return schedule_date_bounds.value.min_date.startOf('month');
});

const max_month = computed(() => {
  return schedule_date_bounds.value.max_date.startOf('month');
});

const can_go_prev_month = computed(() => {
  if (!state.current_month)
    return false;
  return state.current_month.isAfter(min_month.value, 'month');
});

const can_go_next_month = computed(() => {
  if (!state.current_month)
    return false;
  return state.current_month.isBefore(max_month.value, 'month');
});

const current_month_label = computed(() => {
  if (!state.current_month)
    return '';
  return state.current_month.format('MMMM YYYY');
});

const dates = computed(() => {
  if (!state.current_month)
    return {};

  const month_start = state.current_month.startOf('month');
  const month_end = state.current_month.endOf('month');
  const all_dates = getDatesBetween(month_start, month_end);

  const dates_map = {};
  all_dates.forEach((date) => {
    const key = date.format('YYYY-MM-DD');
    const label = dayjs(date).format('DD MMMM YYYY');
    dates_map[key] = {
      label,
    };
  });
  return dates_map;
});

const hot_table_height = computed(() => {
  let calculated_height = (state.hot_data.length * 30) + 87; // 71 - header, 1 - first row, 15 - scrollbar
  if (calculated_height > 550)
    calculated_height = 550;
  if (calculated_height < 200)
    calculated_height = 200;
  return `${calculated_height}px`;
});

const row_metadata = computed(() => {
  const metadata = {};

  state.hot_data.forEach((row, idx) => {
    const task = $g.value.getTask(row.id);
    const activity_uid = task?.uid;
    const is_completed = active_schedule.value.activities?.[activity_uid]?.progress === 1;
    const is_milestone = $g.value.config.types.milestone === task?.type;

    metadata[idx] = {
      is_read_only: is_completed || is_milestone,
      task_resources: task?.resources || [],
      resource_uid: row.resource,
      activity_id: row.id,
    };
  });

  return metadata;
});

const hot_columns = computed(() => {
  const dateColumns = Object.keys(dates.value).map(date => ({
    header: dates.value[date].label,
    columns: [
      {
        data: `${date}_work`,
        header: $t('Work'),
        type: 'numeric',
        width: 80,
        renderer: valuesRenderer,
      },
      {
        data: `${date}_units`,
        header: $t('Units'),
        type: 'numeric',
        width: 80,
        renderer: valuesRenderer,
      },
      {
        data: `${date}_cost`,
        header: $t('Cost'),
        type: 'numeric',
        width: 80,
        renderer: valuesRenderer,
      },
    ],
  }));

  return [
    {
      data: 'id',
      header: $t('ID'),
      readOnly: true,
    },
    {
      data: 'activity',
      header: $t('Activity'),
      readOnly: true,
      wordWrap: false,
    },
    {
      data: 'resource',
      header: $t('Resource'),
      width: 230,
      wordWrap: false,
      readOnly: true,
      renderer: resourceRenderer,
    },
    ...dateColumns.flatMap(dateCol => dateCol.columns),
  ];
});

const hot_nested_headers = computed(() => {
  const firstLevel = [
    $t('ID'),
    $t('Activity'),
    $t('Resource'),
    ...Object.keys(dates.value).map(date => ({ label: dates.value[date].label, colspan: 3 })),
  ];

  const secondLevel = [
    '',
    '',
    '',
    ...Object.keys(dates.value).flatMap(() => [
      $t('Work'),
      $t('Units'),
      $t('Cost'),
    ]),
  ];

  return [firstLevel, secondLevel];
});

const merge_cells = computed(() => {
  const merges = [];

  if (!state.hot_data || state.hot_data.length === 0) {
    return merges;
  }

  let current_id = null;
  let current_activity = null;
  let start_row = 0;

  state.hot_data.forEach((row, index) => {
    const row_id = row.id;
    const row_activity = row.activity;

    if (row_id !== current_id || row_activity !== current_activity) {
      if (current_id !== null && index - start_row > 1) {
        merges.push({
          row: start_row,
          col: 0,
          rowspan: index - start_row,
          colspan: 1,
        });
        merges.push({
          row: start_row,
          col: 1,
          rowspan: index - start_row,
          colspan: 1,
        });
      }

      current_id = row_id;
      current_activity = row_activity;
      start_row = index;
    }
  });

  if (current_id !== null && state.hot_data.length - start_row > 1) {
    merges.push({
      row: start_row,
      col: 0,
      rowspan: state.hot_data.length - start_row,
      colspan: 1,
    });
    merges.push({
      row: start_row,
      col: 1,
      rowspan: state.hot_data.length - start_row,
      colspan: 1,
    });
  }

  return merges;
});

const hierarchy_breadcrumbs = computed(() => {
  const parents = [];
  $g.value.eachParent((task) => {
    parents.push({
      value: task.uid,
      label: task.text,
      uid: task.id,
      has_children: true,
      truncate_length: Math.min(task.text?.length, 20),
    });
  }, props.activity.id);
  return parents.reverse();
});

function valuesRenderer(_instance, td, row, _col, prop, value, cellProperties) {
  const meta = row_metadata.value[row];
  const resource = schedule_resources_map.value[meta?.resource_uid];

  td.classList.add('htMiddle');

  const is_work = prop.includes('_work');
  const is_units = prop.includes('_units');
  const is_cost = prop.includes('_cost');

  const should_be_readonly = meta?.is_read_only
    || (
      is_units
      && (
        !resource
        || resource.cost_type === 'fixed'
      )
    );

  if (should_be_readonly) {
    td.className += ' read-only-cell';
  }
  else {
    td.className = td.className.replace('read-only-cell', '').trim();
  }

  if (cellProperties.valid === false) {
    td.className += ' htInvalid';
  }

  if (value !== null && value !== undefined && value !== '') {
    if (is_cost) {
      td.textContent = `${active_schedule.value.currency.symbol}${value}`;
    }
    else {
      let suffix;
      if (is_work) {
        suffix = $t('units');
      }
      else if (is_units) {
        if (resource?.cost_type === 'per_hour') {
          suffix = $t('hrs');
        }
        else if (resource?.cost_type === 'per_item') {
          suffix = $t('items');
        }
        else {
          suffix = '';
        }
      }
      td.textContent = suffix ? `${value} ${suffix}` : value;
    }
  }
  else {
    td.textContent = '';
  }

  return td;
}

function resourceRenderer(_instance, td, _row, _col, _prop, value, _cellProperties) {
  td.innerHTML = '';
  td.classList.add('htMiddle');

  if (!value) {
    td.textContent = '';
    return td;
  }

  const resource = schedule_resources_map.value[value];
  if (!resource) {
    td.textContent = '';
    return td;
  }

  if (resource.type === 'member') {
    const { members_details } = getUserDetails(resource.external_id);
    const user = members_details?.[0];

    if (user) {
      const display_name = user.name;
      const user_details = {
        bg_color: stringToNumber(user?.name),
        label: DOMPurify.sanitize(display_name.trim(), { ALLOWED_TAGS: [] }),
        id: user.uid,
        avatar: user?.display_picture ?? '',
        email: DOMPurify.sanitize(user?.email, { ALLOWED_TAGS: [] }),
      };

      const container = document.createElement('div');
      container.style.display = 'flex';
      container.classList = 'gap-1';
      container.style.alignItems = 'center';
      container.style.marginBottom = '4px';

      if (user_details.avatar) {
        const img = document.createElement('img');
        img.src = user?.display_picture ?? '';
        img.className = 'rounded-full object-cover !text-xs !w-4 !h-4 flex justify-center';
        img.style.width = '24px';
        img.style.height = '24px';
        container.appendChild(img);
      }
      else {
        const span = document.createElement('span');
        span.className = 'rounded-full !text-xs !w-4 !h-4 flex justify-center';
        span.style.width = '24px';
        span.style.height = '24px';
        span.style.backgroundColor = user_details?.bg_color;
        span.textContent = user_details.label?.substring(0, 1);
        span.style.color = 'white';
        span.style.alignContent = 'center';
        container.appendChild(span);
      }

      const name = document.createElement('span');
      name.textContent = user_details.label;
      container.appendChild(name);
      td.appendChild(container);
    }
  }
  else if (resource.type === 'custom') {
    td.textContent = resource.name;
  }

  return td;
}

function checkForUnsavedChanges() {
  if (!state.original_hot_data || !state.hot_data)
    return false;
  return JSON.stringify(state.hot_data) !== JSON.stringify(state.original_hot_data);
}

function navigateMonth(direction) {
  if (!can_go_prev_month.value && direction === 'prev')
    return;
  if (!can_go_next_month.value && direction === 'next')
    return;

  const new_month = direction === 'prev'
    ? state.current_month.subtract(1, 'month')
    : state.current_month.add(1, 'month');

  if (direction === 'prev' && new_month.isBefore(min_month.value, 'month'))
    return;
  if (direction === 'next' && new_month.isAfter(max_month.value, 'month'))
    return;

  if (checkForUnsavedChanges()) {
    unsaved_changes_modal.patchOptions({
      attrs: {
        content: $t('You have unsaved changes. Do you want to save them before switching months?'),
        async save() {
          await onSave();
          state.current_month = new_month;
          unsaved_changes_modal.close();
        },
        onLeave() {
          state.current_month = new_month;
          unsaved_changes_modal.close();
        },
        onClose() {
          unsaved_changes_modal.close();
        },
      },
    });
    unsaved_changes_modal.open();
  }
  else {
    state.current_month = new_month;
  }
}

function getDatesBetween(min_date, max_date) {
  const dates = [];
  let current_date = min_date || dayjs(min_date);
  const end_date = max_date || dayjs(max_date);
  while (current_date.isSameOrBefore(end_date)) {
    dates.push(current_date);
    current_date = current_date.add(1, 'day');
  }
  return dates;
}

function beforeBeginEditing(row, col) {
  const column = hot_columns.value[col];
  const meta = row_metadata.value[row];

  if (!meta)
    return;

  if (meta.is_read_only) {
    return false;
  }

  if (column.data === 'resource') {
    return false;
  }

  if (column.data?.includes('_units')) {
    const resource = schedule_resources_map.value[meta.resource_uid];

    if (!resource) {
      return false;
    }

    const can_edit = resource.cost_type === 'per_hour' || resource.cost_type === 'per_item';

    if (!can_edit) {
      return false;
    }
  }
}

const debouncedAfterChange = debounce((changes, source) => {
  if (source === 'CopyPaste.paste' || source !== 'edit')
    return;

  changes?.forEach?.((change) => {
    if (change[1].endsWith('_units')) {
      const rowData = state.hot_instance.getSourceDataAtRow(change[0]);
      updateCostBasedOnUnits(change[0], rowData.resource, change[3], change[1]);
    }
  });
}, 100);

function afterChange(changes, source) {
  debouncedAfterChange(changes, source);
}

function beforeKeyDown(event) {
  logger.log('beforeKeyDown', event);
}

function beforeChange() {
  // For some reason, the members selection is failing if this empty beforeChange function is not called
}

function onHandsOnTableReady(hot_instance) {
  state.hot_instance = hot_instance;

  state.hot_instance.updateSettings({
    beforeBeginEditing,
  });

  applyMergeCells();

  state.hot_instance.render();
}

function applyMergeCells() {
  if (!state.hot_instance)
    return;

  const mergeCellsPlugin = state.hot_instance.getPlugin('mergeCells');

  if (mergeCellsPlugin) {
    mergeCellsPlugin.clearCollections();
    merge_cells.value.forEach((merge) => {
      mergeCellsPlugin.merge(merge.row, merge.col, merge.row + merge.rowspan - 1, merge.col + merge.colspan - 1);
    });
    state.hot_instance.render();
  }
}

function groupTrackingsByActivityResource(activity_uids) {
  const relevant_trackings = active_schedule.value.trackings?.filter(tracking =>
    activity_uids.includes(tracking.activity),
  ) || [];

  const trackings_by_activity_resource = {};
  relevant_trackings.forEach((tracking) => {
    const tracking_start = dayjs.utc(tracking.start).format('YYYY-MM-DD');
    const date_key = tracking_start;

    if (dates.value[date_key]) {
      const key = `${tracking.activity}–${tracking.resource || 'no_resource'}`;
      if (!trackings_by_activity_resource[key]) {
        trackings_by_activity_resource[key] = [];
      }
      trackings_by_activity_resource[key].push(tracking);
    }
  });

  return trackings_by_activity_resource;
}

function createBaseRowData(item, resource_uid = null) {
  const baseData = {
    id: item.id,
    activity: item.text,
    resource: resource_uid || item.resource,
  };

  Object.keys(dates.value).forEach((date) => {
    baseData[`${date}_work`] = null;
    baseData[`${date}_cost`] = null;
    baseData[`${date}_units`] = null;
    baseData[`${date}_tracking_uid`] = null;
  });

  return baseData;
}

function fillRowWithTrackingData(baseData, trackings, resource_uid) {
  trackings.forEach((tracking) => {
    const tracking_start = dayjs.utc(tracking.start).format('YYYY-MM-DD');
    const date_key = tracking_start;

    if (dates.value[date_key]) {
      baseData[`${date_key}_work`] = tracking.work;
      baseData[`${date_key}_cost`] = tracking.cost;

      const resource = schedule_resources_map.value[resource_uid];
      if (resource && tracking.units) {
        baseData[`${date_key}_units`] = tracking.units;
      }

      baseData[`${date_key}_tracking_uid`] = tracking.uid;
    }
  });
}

function createRowsForActivity(item, trackings_by_activity_resource) {
  const rows = [];
  const activity_uid = $g.value.getTask(item.id)?.uid;
  const task = $g.value.getTask(item.id);
  const task_resources = task?.resources || [];

  const resources_with_trackings = new Set();

  Object.keys(trackings_by_activity_resource).forEach((key) => {
    if (key.startsWith(`${activity_uid}–`)) {
      const resource_uid = key.split('–')[1];
      if (resource_uid !== 'no_resource') {
        resources_with_trackings.add(resource_uid);
      }
    }
  });

  const all_resources = new Set([...task_resources, ...resources_with_trackings]);

  let is_first_row = true;

  const no_resource_key = `${activity_uid}–no_resource`;
  const no_resource_trackings = trackings_by_activity_resource[no_resource_key] || [];
  const no_resource_row = createBaseRowData(item, null);
  fillRowWithTrackingData(no_resource_row, no_resource_trackings, null);
  rows.push(no_resource_row);
  is_first_row = false;

  all_resources.forEach((resource_uid) => {
    const key = `${activity_uid}–${resource_uid}`;
    const trackings = trackings_by_activity_resource[key] || [];

    const baseData = createBaseRowData(item, resource_uid);
    fillRowWithTrackingData(baseData, trackings, resource_uid);

    if (!is_first_row) {
      baseData.is_duplicated = true;
    }
    is_first_row = false;

    rows.push(baseData);
  });

  return rows;
}

function generateInitialData() {
  state.activities = [];
  if ([$g.value.config.types.task, $g.value.config.types.milestone].includes(props.activity.type)) {
    state.activities = [props.activity];
  }
  else {
    state.activities = $g.value.getChildren(props.activity.id)
      .map(child => $g.value.getTask(child))
      .filter((child) => {
        return [$g.value.config.types.task, $g.value.config.types.milestone].includes(child.type);
      });
  }

  const activity_uids = state.activities.map(activity => $g.value.getTask(activity.id)?.uid);
  const trackings_by_activity_resource = groupTrackingsByActivityResource(activity_uids);

  const rows = [];
  state.activities.forEach((item) => {
    rows.push(...createRowsForActivity(item, trackings_by_activity_resource));
  });

  return rows;
}

function clearTableData() {
  if (!state.hot_instance) {
    state.hot_data = generateInitialData();
    return;
  }

  const activity_uids = state.activities.map(activity => $g.value.getTask(activity.id)?.uid);
  const trackings_by_activity_resource = groupTrackingsByActivityResource(activity_uids);

  const rows = [];
  state.activities.forEach((item) => {
    rows.push(...createRowsForActivity(item, trackings_by_activity_resource));
  });

  state.hot_data = rows;
}

function afterGetRowHeader(row, th) {
  const row_data = state.hot_instance?.getSourceDataAtRow?.(row);
  if (row_data?.is_duplicated) {
    th.innerHTML = `
    <div class="flex items-center gap-1">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 15C4.06812 15 3.60218 15 3.23463 14.8478C2.74458 14.6448 2.35523 14.2554 2.15224 13.7654C2 13.3978 2 12.9319 2 12V5.2C2 4.0799 2 3.51984 2.21799 3.09202C2.40973 2.71569 2.71569 2.40973 3.09202 2.21799C3.51984 2 4.0799 2 5.2 2H12C12.9319 2 13.3978 2 13.7654 2.15224C14.2554 2.35523 14.6448 2.74458 14.8478 3.23463C15 3.60218 15 4.06812 15 5M12.2 22H18.8C19.9201 22 20.4802 22 20.908 21.782C21.2843 21.5903 21.5903 21.2843 21.782 20.908C22 20.4802 22 19.9201 22 18.8V12.2C22 11.0799 22 10.5198 21.782 10.092C21.5903 9.71569 21.2843 9.40973 20.908 9.21799C20.4802 9 19.9201 9 18.8 9H12.2C11.0799 9 10.5198 9 10.092 9.21799C9.71569 9.40973 9.40973 9.71569 9.21799 10.092C9 10.5198 9 11.0799 9 12.2V18.8C9 19.9201 9 20.4802 9.21799 20.908C9.40973 21.2843 9.71569 21.5903 10.092 21.782C10.5198 22 11.0799 22 12.2 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg> ${row + 1}
    </div>
    `;
  }
  th.classList.add('htMiddle');
}

function updateCostBasedOnUnits(row, resource_uid, units_value, units_key) {
  if (isNil(units_value) || units_value === '')
    return;
  const selected_resource_uid = resource_uid;
  const resource = active_schedule.value.resources.find(
    r => r.uid === selected_resource_uid,
  );
  if (resource) {
    const rate = resource.cost || 0;
    const value = Number(units_value) || 0;
    const cost = rate * value;
    const dateKey = units_key.replace(/_units$/, '');
    const costKey = `${dateKey}_cost`;
    state.hot_instance.setDataAtRowProp(row, costKey, cost, 'auto');
  }
}

function beforeCellCopyInterceptor(_data, copiedValue, _dataRowIndex, _dataColIndex, _cellMeta, _row, col) {
  const resource_col_index = hot_columns.value.findIndex(column => column.data === 'resource');

  if (col !== resource_col_index) {
    return undefined;
  }

  if (copiedValue) {
    const resource = schedule_resources_map.value[copiedValue];
    if (resource) {
      if (resource.type === 'member') {
        const member_details = getUserDetails([resource.external_id]).members_details[0];
        return member_details?.name || copiedValue;
      }
      else if (resource.type === 'custom') {
        return resource.name;
      }
    }
  }
  return copiedValue;
}

function beforeCellPasteInterceptor(data, pastedValue, dataRowIndex, dataColIndex, cellMeta, cellData, row, col) {
  const meta = row_metadata.value[row];
  if (meta?.is_read_only) {
    data[dataRowIndex % data.length][dataColIndex % data[0].length] = cellData;
    return;
  }

  if (cellMeta.prop === 'resource') {
    data[dataRowIndex % data.length][dataColIndex % data[0].length] = cellData;
    return;
  }

  if (cellMeta.prop?.includes?.('_units')) {
    const resource_col_index = hot_columns.value.findIndex(column => column.data === 'resource');
    const paste_resource_col_index = resource_col_index - col + dataColIndex;

    let target_resource_uid = null;

    if (paste_resource_col_index >= 0 && paste_resource_col_index < data[0].length) {
      target_resource_uid = data[dataRowIndex % data.length][paste_resource_col_index % data[0].length];
    }

    if (!target_resource_uid) {
      const row_data = state.hot_instance.getData()[row];
      target_resource_uid = row_data?.[resource_col_index];
    }

    if (target_resource_uid) {
      const resource = active_schedule.value.resources.find((res) => {
        return res.uid === target_resource_uid;
      });

      if (resource) {
        const can_paste = resource.cost_type === 'per_hour' || resource.cost_type === 'per_item';

        if (can_paste) {
          data[dataRowIndex % data.length][dataColIndex % data[0].length] = pastedValue;
          if (pastedValue) {
            const dateKey = cellMeta.prop.replace(/_units$/, '');
            const costKey = `${dateKey}_cost`;
            const cost_col_index = hot_columns.value.findIndex(column => column.data === costKey);
            const paste_cost_col_index = cost_col_index - col + dataColIndex;

            const is_cost_being_pasted = paste_cost_col_index >= 0
              && paste_cost_col_index < data[0].length
              && data[dataRowIndex % data.length][paste_cost_col_index % data[0].length] != null
              && data[dataRowIndex % data.length][paste_cost_col_index % data[0].length] !== '';

            if (!is_cost_being_pasted) {
              setTimeout(() => {
                updateCostBasedOnUnits(row, target_resource_uid, pastedValue, cellMeta.prop);
              }, 0);
            }
          }
        }
        else {
          data[dataRowIndex % data.length][dataColIndex % data[0].length] = cellData;
        }
      }
      else {
        data[dataRowIndex % data.length][dataColIndex % data[0].length] = cellData;
      }
    }
    else {
      data[dataRowIndex % data.length][dataColIndex % data[0].length] = cellData;
    }
  }
}

function afterPaste(_data, _coords) {
  setTimeout(() => {
    state.hot_instance.render();
  }, 50);
}

function beforeOnCellContextMenu(event, _coords, _td) {
  const selectedRange = state.hot_instance.getSelectedRangeLast();

  const isSingleRow = selectedRange.from.row === selectedRange.to.row;
  const isNotHeader = selectedRange.from.row >= 0;

  if (!isSingleRow || !isNotHeader) {
    event.stopImmediatePropagation();
    return false;
  }

  return true;
}

function isTrackingUnchanged(row_index, start) {
  if (!state.original_hot_data || !state.original_hot_data[row_index]) {
    return false;
  }

  const original_row = state.original_hot_data[row_index];
  const current_row = state.hot_data[row_index];

  const original_work = original_row[`${start}_work`];
  const original_cost = original_row[`${start}_cost`];
  const original_units = original_row[`${start}_units`];
  const original_tracking_uid = original_row[`${start}_tracking_uid`];

  const current_work = current_row[`${start}_work`];
  const current_cost = current_row[`${start}_cost`];
  const current_units = current_row[`${start}_units`];
  const current_tracking_uid = current_row[`${start}_tracking_uid`];

  if (!current_tracking_uid) {
    return false;
  }

  return original_work === current_work
    && original_cost === current_cost
    && original_units === current_units
    && original_tracking_uid === current_tracking_uid;
}

async function onSave() {
  state.is_saving = true;

  try {
    const payload_with_metadata = [];

    state.hot_data.forEach((row, row_index) => {
      const date_ranges = Object.keys(row)
        .filter(key => key.match(/^\d{4}-\d{2}-\d{2}/))
        .map((key) => {
          const [start] = key.split('_').slice(0, 1);
          return { start };
        })
        .filter((value, index, self) =>
          index === self.findIndex(t => t.start === value.start),
        );

      date_ranges.forEach(({ start }) => {
        const work = row[`${start}_work`];
        const cost = row[`${start}_cost`];
        const units = row[`${start}_units`];
        const tracking_uid = row[`${start}_tracking_uid`];

        if (work || cost || units || tracking_uid) {
          const activity_uid = $g.value.getTask(row.id).uid;

          if (tracking_uid && isTrackingUnchanged(row_index, start)) {
            return;
          }

          const tracking_data = {
            activity: activity_uid,
            resource: row.resource || null,
            start: dayjs(start).utc(true).startOf('day').toISOString(),
            end: dayjs(start).utc(true).endOf('day').toISOString(),
            work,
            cost,
            units,
          };

          if (tracking_uid) {
            tracking_data.uid = tracking_uid;
          }

          payload_with_metadata.push({
            tracking: tracking_data,
            row_index,
            date_key: start,
          });
        }
      });
    });

    const create = [];
    const update = [];
    const deletes = [];

    payload_with_metadata.forEach(({ tracking }) => {
      if (tracking.uid) {
        if (
          (isNil(tracking.work) || tracking.work === '')
          && (isNil(tracking.cost) || tracking.cost === '')
          && (isNil(tracking.units) || tracking.units === '')
        ) {
          deletes.push(tracking);
        }
        else {
          update.push(tracking);
        }
      }
      else {
        create.push(tracking);
      }
    });

    const payload = { create, update, delete: deletes };
    const response = await update_trackings(payload);

    if (response?.created) {
      response.created.forEach((created_tracking) => {
        const metadata_entry = payload_with_metadata.find(({ tracking }) =>
          !tracking.uid
          && tracking.activity === created_tracking.activity
          && tracking.resource === created_tracking.resource
          && tracking.start === created_tracking.start,
        );

        if (metadata_entry) {
          const { row_index, date_key } = metadata_entry;
          const tracking_uid_key = `${date_key}_tracking_uid`;
          state.hot_data[row_index][tracking_uid_key] = created_tracking.uid;
        }
      });
    }

    state.original_hot_data = JSON.parse(JSON.stringify(state.hot_data));
    $toast({
      title: payload.create.length + payload.update.length + payload.delete.length > 1 ? $t('Activities tracked successfully') : $t('Activity tracked successfully'),
      text: $t('Your changes have been saved'),
      type: 'success',
    });
  }
  catch (error) {
    $toast({
      title: $t('Something went wrong'),
      text: $t('Please try again'),
      type: 'error',
    });
    logger.error(error);
  }
  finally {
    state.is_saving = false;
  }
}

const debouncedValidation = debounce(() => {
  state.hot_instance?.validateCells?.((is_valid) => {
    state.is_save_disabled = !is_valid;
  });
}, 200);

function initializeCurrentMonth() {
  const current_month = dayjs().startOf('month');

  if (current_month.isSameOrAfter(min_month.value, 'month') && current_month.isSameOrBefore(max_month.value, 'month')) {
    state.current_month = current_month;
  }
  else {
    const diff_to_min = Math.abs(current_month.diff(min_month.value, 'month'));
    const diff_to_max = Math.abs(current_month.diff(max_month.value, 'month'));

    if (diff_to_min <= diff_to_max) {
      state.current_month = min_month.value;
    }
    else {
      state.current_month = max_month.value;
    }
  }
}

watch(() => state.current_month, () => {
  clearTableData();
  state.hot_instance = null;
  state.force_update++;
  nextTick(() => {
    state.original_hot_data = JSON.parse(JSON.stringify(state.hot_data));
  });
}, { deep: true });

watch(() => state.hot_data, () => {
  debouncedValidation();
}, { deep: true });

watch(() => hot_table_height.value, () => {
  state.hot_instance?.updateSettings({
    height: hot_table_height.value,
  });
});

watch(() => state.is_maximized, () => {
  nextTick(() => {
    const newHeight = state.is_maximized ? '100%' : hot_table_height.value;
    state.hot_instance?.updateSettings({
      height: newHeight,
    });
  });
});

onMounted(() => {
  initializeCurrentMonth();
  state.hot_data = generateInitialData();
  nextTick(() => {
    state.original_hot_data = JSON.parse(JSON.stringify(state.hot_data));
  });
});
</script>

<template>
  <HawkModalContainer
    :options="{ teleportTo: is_fullscreen ? '#pm-fullscreen-container' : 'body', escToClose: false }"
    :content_class="state.is_maximized ? 'h-full w-full rounded-none' : 'w-[80vw]'"
  >
    <HawkModalHeader @close="emit('close')">
      <template #title>
        <HawkBreadcrumbs
          :items="hierarchy_breadcrumbs"
          :show_active_color="false"
          :max_tags_to_display="3"
        />
        <div>
          <template v-if="state.activities.length > 1">
            {{ $t('Track the subtasks of') }} <span class="text-primary-600">{{ props.activity.text }} ({{ $g.getWBSCode(props.activity) }})</span>
          </template>
          <template v-else>
            {{ $t('Track') }} <span class="text-primary-600">{{ props.activity.text }} ({{ $g.getWBSCode(props.activity) }})</span>
          </template>
        </div>
      </template>
      <template #right>
        <IconHawkMaximizeOne
          v-if="!state.is_maximized"
          class="cursor-pointer"
          @click="state.is_maximized = true"
        />
        <IconHawkMinimizeOne
          v-else
          class="cursor-pointer"
          @click="state.is_maximized = false"
        />
      </template>
    </HawkModalHeader>
    <HawkModalContent :class="state.is_maximized ? '!h-[calc(100vh-180px)] !max-h-[calc(100vh-180px)]' : ''">
      <div class="flex justify-between items-center mb-6">
        <div class="text-sm font-normal text-gray-900">
          {{ $t('Track work done, amount spent for an activity, or record for a particular resource.') }}
        </div>
        <div class="flex items-center">
          <div class="flex items-center gap-1">
            <IconHawkChevronLeft
              class="cursor-pointer"
              :class="{
                '!cursor-not-allowed text-gray-300': !can_go_prev_month,
              }"
              @click="navigateMonth('prev')"
            />
            <span class="text-sm font-medium text-gray-900 min-w-[140px] text-center">
              {{ current_month_label }}
            </span>
            <IconHawkChevronRight
              class="cursor-pointer"
              :class="{
                '!cursor-not-allowed text-gray-300': !can_go_next_month,
              }"
              @click="navigateMonth('next')"
            />
          </div>
        </div>
      </div>
      <div
        v-if="state.hot_data.length"
        :style="{ height: state.is_maximized ? '90%' : hot_table_height }"
        class="pm-excel-modal"
      >
        <div v-if="state.hot_instance === null" class="absolute top-0 left-0 z-10 flex items-center justify-center w-full h-full">
          <HawkLoader class="z-10" />
        </div>
        <HawkHandsOnTable
          :key="state.force_update"
          ref="hot$"
          :hot-settings="{
            afterChange,
            beforeChange,
            beforeKeyDown,
            afterGetRowHeader,
            afterPaste,
            nestedRows: false,
            autoRowSize: true,
            rowHeights: '30px',
            bindRowsWithHeaders: true,
            viewportColumnRenderingOffset: 40,
            nestedHeaders: hot_nested_headers,
            fixedColumnsStart: 3,
            className: 'htMiddle',
            beforeOnCellContextMenu,
            contextMenu: false,
            dropdownMenu: false,
            columnSorting: false,
            headerClassName: 'htCenter',
            mergeCells: true,
          }"
          :apply-read-only-class="false"
          :right-click-menu="{}"
          :data="state.hot_data"
          :columns="hot_columns"
          :columns-menu="{ items: {} }"
          :before-cell-paste-interceptor="beforeCellPasteInterceptor"
          :before-cell-copy-interceptor="beforeCellCopyInterceptor"
          :add-new-row-on-enter="false"
          :height="state.is_maximized ? '100%' : hot_table_height"
          @ready="onHandsOnTableReady"
        />
      </div>
    </HawkModalContent>
    <HawkModalFooter>
      <template #right>
        <div class="flex justify-end w-full col-span-full">
          <HawkButton
            type="outlined"
            class="mr-4"
            @click="emit('close')"
          >
            {{ $t('Cancel') }}
          </HawkButton>
          <HawkButton
            color="primary"
            :loading="state.is_saving"
            :disabled="state.is_save_disabled"
            @click="onSave"
          >
            {{ $t('Save') }}
          </HawkButton>
        </div>
      </template>
    </HawkModalFooter>
  </HawkModalContainer>
</template>

<style lang="scss">
.pm-excel-modal {
  .changeType {
    @apply hidden;
  }

  .handsontableInput {
    line-height: 29px;
  }

  th.afterHiddenColumn::before{
    content: '' !important;
  }

  .htCommentCell {
    @apply bg-[#FFBEBA];
  }

  td.current strong {
    font-weight: 400 !important;
  }
}
</style>
